
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import TitleDetailStreamingChartListItem from '@/components/details/streaming-chart/TitleDetailStreamingChartListItem.vue';

import { ObjectType, StreamingChartsTitlesEdge } from '@/@types/graphql-types';
import { HomeModuleTitleFragment } from '@/components/home/graphql/queries/GetHomeModulesV2.query';
import { streamingChartLandingPageCountries } from '@/components/details/streaming-chart/streaming-chart-config';

import { timestamp } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { trackTitleDetailRedesign } from '@/pages/experiments/title-detail-redesign/tracking';

const Language = namespace('language');

@Component({
	name: 'TitleDetailStreamingChartList',
	components: { TitleDetailStreamingChartListItem },
})
export default class TitleDetailStreamingChartList extends Vue {
	@Prop({ required: true }) titles: StreamingChartsTitlesEdge[];
	@Prop() titleObjectType: ObjectType;
	@Prop() lastUpdated: string | number | Date;
	@Prop({ default: '' }) titleId: string;
	@Prop({ default: false }) isSidebar: boolean;

	@Language.Getter language: string;
	@Language.Getter country: string;

	@Emit('clicked')
	onClick(title: HomeModuleTitleFragment, index: number) {
		//TITLE_DETAIL_REDESIGN
		trackTitleDetailRedesign({ action: 'click', label: 'rank_title_clicked' });
		//TITLE_DETAIL_REDESIGN
		return { title, index };
	}

	getChartPosition(title: StreamingChartsTitlesEdge) {
		return title?.streamingChartInfo?.rank;
	}

	getChartPositionDynamic(title: StreamingChartsTitlesEdge) {
		return title?.streamingChartInfo?.trend;
	}

	getChartMovement(title: StreamingChartsTitlesEdge) {
		return title?.streamingChartInfo?.trendDifference;
	}

	getDaysInTop1000(title: StreamingChartsTitlesEdge) {
		return title.streamingChartInfo?.daysInTop1000;
	}

	getTitleName(titleNode: HomeModuleTitleFragment) {
		if (titleNode.objectType === ObjectType.Movie || titleNode.objectType === ObjectType.Show) {
			return titleNode?.content?.title;
		}

		if (titleNode.__typename === 'Season') {
			return titleNode?.show?.content?.title;
		}

		return '';
	}

	getShowSeason(titleNode: HomeModuleTitleFragment) {
		if (titleNode.__typename === 'Season') {
			return titleNode?.content?.seasonNumber;
		}
		return null;
	}

	isTitle(title: StreamingChartsTitlesEdge) {
		return title?.node?.id === this.titleId;
	}

	//TITLE_DETAIL_REDESIGN
	trackLinkClick() {
		TrackingHelper.trackEvent('userinteraction', {
			action: 'redirect_clicked',
			label: 'streaming_chart',
		});
		trackTitleDetailRedesign({ action: 'click', label: 'popular_titles_clicked' });
	}
	//TITLE_DETAIL_REDESIGN

	get showChart() {
		return this.titles?.length > 0;
	}

	get updatedTimestamp() {
		const updated = timestamp(
			this.lastUpdated,
			{
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
			},
			this.country,
			this.language
		);
		return `${updated.time}, ${updated.date}`;
	}

	get showLinkButton() {
		return !!streamingChartLandingPageCountries.includes(this.country) && this.language === 'en';
	}

	get linkButtonText() {
		if (this.titleObjectType === ObjectType.Movie) return 'Show all movies in the JustWatch Streaming Charts';
		if (this.titleObjectType === ObjectType.Show) return 'Show all TV shows in the JustWatch Streaming Charts';
		if (this.titleObjectType === ObjectType.ShowSeason) return 'Show all seasons in the JustWatch Streaming Charts';
		return 'Show all titles in the JustWatch Streaming Charts';
	}

	get linkButtonQueryParams() {
		let objectTypeQueryParam = undefined;
		if (this.titleObjectType === ObjectType.Show) objectTypeQueryParam = 'shows';
		if (this.titleObjectType === ObjectType.ShowSeason) objectTypeQueryParam = 'seasons';
		return {
			t: objectTypeQueryParam,
			ct: 'daily',
		};
	}
}
